import type { IconGlyph } from '@sunwebgroup/uds-ui/icon'
import { Dialog } from '@sunwebgroup/uds-ui/dialog'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { useMount } from 'react-use'
import { useUser } from '@/domain/user/hooks'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import QSClient from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import { useSetActiveTab } from '../domain/hooks'
import groupResponsibleUseCases from '../domain/useCases'
import groupResponsibleAPIClient from '../infrastructure/groupResponsibleAPIClient'
import { groupResponsibleStore } from '../infrastructure/store'
import { useGroupResponsibleStore } from '../infrastructure/useGroupResponsibleStore'
import { BusTable } from './Components/BusTable'
import { DownloadButton } from './Components/downloadButton/DownloadButton'
import { Header } from './Components/Header'
import { Hero } from './Components/Hero'
import { ParticipantTable } from './Components/ParticipantTable'
import { RoomsTable } from './Components/RoomsTable'
import { GroupResponsibleDependenciesProvider, useGroupResponsibleUseCases } from './dependencies'
import styles from './index.module.css'
import { isBusTabActive, isParticipantsTabActive, isRoomsTabActive } from './selectors'
import { getEditDeadLine } from './selectors/commonSelectors'

interface StatusNotificationProps {
  iconGlyph: IconGlyph
  title: string
  message: string
}

const queryClient = new QueryClient()

const StatusNotification: React.FC<StatusNotificationProps> = ({ iconGlyph, title, message }) => {
  const intl = useIntl()

  return (
    <div className={styles.centeredContent}>
      <div className={styles.statusNotification}>
        <Dialog
          type="custom"
          icon={iconGlyph}
          text={intl.formatMessage({ id: title })}
          supportingText={intl.formatMessage({ id: message })}
        />
      </div>
    </div>
  )
}

export function GroupsResponsible() {
  const locale = useConfiguration('locale')
  const dependencies = React.useMemo(() => {
    const useCases = groupResponsibleUseCases({
      apiClient: groupResponsibleAPIClient,
      store: groupResponsibleStore,
      queryStringClient: QSClient,
      locale,
    })

    return {
      groupResponsibleAPIClient,
      groupResponsibleStore,
      useCases,
    }
  }, [locale])
  return (
    <GroupResponsibleDependenciesProvider dependencies={dependencies}>
      <QueryClientProvider client={queryClient}>
        <div className={styles.pageLayout}>
          <Header />
          <Page />
        </div>
      </QueryClientProvider>
    </GroupResponsibleDependenciesProvider>
  )
}

function Page() {
  const locale = useConfiguration('locale')
  const useCases = useGroupResponsibleUseCases()
  const { isLoggedIn } = useUser()
  useSetActiveTab()
  useMount(() => useCases.pageLoaded())
  const loading = useGroupResponsibleStore(state => state.retrieveBookingData.loading)
  const error = useGroupResponsibleStore(state => state.retrieveBookingData.error)
  const { deadLineDate: busDeadlineDate, daysBefore: busDaysBefore } = useGroupResponsibleStore(state => getEditDeadLine(state, locale, 'bus'))
  const intl = useIntl()
  const isClosedSubscriptionModel = useGroupResponsibleStore(state => state.bookingData.group.subscriptionModel === 'Closed')
  const activeTabs = {
    participants: useGroupResponsibleStore(isParticipantsTabActive),
    rooms: useGroupResponsibleStore(isRoomsTabActive),
    bus: useGroupResponsibleStore(isBusTabActive),
  }

  if (loading) {
    return <LoadingState />
  }

  if (error) {
    return (
      <StatusNotification
        iconGlyph="FaceSad"
        title="groups_responsible_error_title"
        message="groups_responsible_error_message"
      />
    )
  }

  if (!isLoggedIn) {
    return (
      <StatusNotification
        iconGlyph="PersonInCircle"
        title="groups_responsible_not_login_title"
        message="groups_responsible_not_login_message"
      />
    )
  }

  return (
    <>
      <Hero />
      <div className={styles.tabsWrapper}>
        {activeTabs.participants && (
          <TabSection
            title={intl.formatMessage({ id: 'common_participants' })}
            subtitle={isClosedSubscriptionModel ? intl.formatMessage({ id: 'groups_responsible_closed_subscription_notice' }) : undefined}
            Component={ParticipantTable}
          />
        )}
        {activeTabs.rooms && (
          <TabSection title={intl.formatMessage({ id: 'groups_responsible_table_rooms' })} Component={RoomsTable} />
        )}
        {activeTabs.bus && (
          <TabSection
            title={intl.formatMessage({ id: 'groups_responsible_table_bus_schedule' })}
            subtitle={intl.formatMessage(
              {
                id: 'groups_responsible_table_bus_assignment_subtitle',
              },
              {
                deadLineDate: busDeadlineDate,
                daysBefore: busDaysBefore,
              },
            )}
            Component={BusTable}
          />
        )}
      </div>
    </>
  )
}

function LoadingState() {
  return (
    <div className={styles.centeredContent}>
      <IndeterminateProgressCircle size={32} />
    </div>
  )
}

function TabSection({
  title,
  subtitle,
  Component,
}: {
  title: string
  subtitle?: string
  Component: React.ComponentType
}) {
  return (
    <div>
      <div className={styles.tableTitleWrapper}>
        <Typography as="h2" className={styles.tabTitleDesktop} variant="display-c-regular">
          {title}
        </Typography>
        <Typography as="h2" className={styles.tabTitleMobile} variant="display-e-regular">
          {title}
        </Typography>
        <DownloadButton />
      </div>
      {subtitle && <Typography as="p" className={styles.tabSubtitle} variant="body-m-regular-snug">{subtitle}</Typography>}
      <Component />
    </div>
  )
}
